import { BoxV2 as Box, Flex } from 'portal-commons';
import { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { faPen, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { attachmentController } from '@/apis/core';
import {
  BrandAssetStatus,
  BrandAssetType,
} from '@/applications/CspPortal/Brands/types';
import { snakeToTitleCase } from '@/utils';

/**
 * Props interface for the CampaignAsset component
 * @interface CampaignAssetProps
 * @property {boolean} [disabled] - Whether the asset is disabled
 * @property {BrandAssetType} type - The type of campaign asset
 * @property {string} url - The URL of the asset
 * @property {string} name - The name of the asset
 * @property {BrandAssetStatus} [status] - The verification status of the asset
 * @property {('verify'|'edit')} [action] - Determines the action button type:
 *   - 'verify': (default) Shows a verify button for pending assets
 *   - 'edit': Shows an edit button for modifying the asset
 * @property {() => void} [onVerify] - Callback function when verify button is clicked
 * @property {() => void} [onEdit] - Callback function when edit button is clicked
 * @property {{width: string, height: string}} [dimension] - The dimensions of the asset
 */
interface CampaignAssetProps {
  disabled?: boolean;
  selected?: boolean;
  type: BrandAssetType;
  uuid: string;
  url?: string;
  name: string;
  status?: BrandAssetStatus;
  action?: 'verify' | 'edit' | null;
  dimension?: { width: string; height: string };
  onVerify?: () => void;
  onEdit?: () => void;
  onSelect?: (name: string) => void;
  [key: `data-${string}`]: unknown;
}

const CampaignAsset: FunctionComponent<CampaignAssetProps> = ({
  disabled,
  selected = false,
  type,
  uuid,
  url,
  name,
  status,
  action = 'verify',
  dimension,
  onVerify,
  onEdit,
  onSelect,
  ...props
}) => {
  const [imageUrl, setImageUrl] = useState(url);
  const imageDimension = dimension || {
    width: type === BrandAssetType.Logo ? '152px' : '379px',
    height: type === BrandAssetType.Logo ? '152px' : '127px',
  };

  const handleVerifyClick = (event: MouseEvent) => {
    event.preventDefault();
    onVerify?.();
  };

  useEffect(() => {
    if (uuid) {
      (async () => {
        const file = await attachmentController.downloadAttachment({ uuid });
        if (file) {
          const url = URL.createObjectURL(file);
          setImageUrl(url);
        }
      })();
    }
  }, [uuid]);

  useEffect(() => {
    if (url) {
      setImageUrl(url);
    }
  }, [url]);

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        width: 'fit-content',
        height: 'fit-content',
        border: selected ? '2px solid #999FA0' : '1px solid #CCCFD0',
        borderRadius: '4px',
        background: '#F2F3F3',
        '&:hover': {
          borderColor: !status ? '#CCCFD0' : '#999FA0',
        },
      }}
      onClick={() => {
        if (
          status === BrandAssetStatus.Pending ||
          status === BrandAssetStatus.Verified
        ) {
          onSelect?.(uuid);
        }
      }}
      data-testid={
        props['data-testid']
          ? `CampaignAsset-${props['data-testid']}`
          : undefined
      }
    >
      <Box
        sx={{
          width: imageDimension.width,
          height: imageDimension.height,
          borderTopRightRadius: '4px',
          borderTopLeftRadius: '4px',
          background: '#FFF',
          overflow: 'hidden',
        }}
      >
        {imageUrl ? (
          <img
            style={{
              width: '100%',
              height: '100%',
              objectFit: type === BrandAssetType.Logo ? 'contain' : 'cover',
            }}
            src={imageUrl}
          />
        ) : (
          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#808789" />
          </Flex>
        )}
      </Box>
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '6px',
          padding: `6px 10px ${status ? '19px' : '10px'}`,
          fontWeight: 400,
        }}
      >
        <Box
          sx={{
            color: '#4D5659',
            fontSize: '14px',
            lineHeight: '16px',
            wordWrap: 'break-word',
            maxWidth: parseInt(imageDimension.width) - 20,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
          title={name}
        >
          {name}
        </Box>
        {status ? (
          <Flex sx={{ alignItems: 'center', gap: '4px' }}>
            <Box
              sx={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background:
                  status === BrandAssetStatus.Verified ? '#23B571' : '#D41C62',
              }}
            />
            <Box
              sx={{
                color: '#808789',
                fontSize: '12px',
                lineHeight: '14px',
              }}
            >
              {snakeToTitleCase(status)}
            </Box>
          </Flex>
        ) : action === 'edit' ? (
          <Flex
            sx={{
              alignItems: 'center',
              gap: 'xxs',
              fontSize: 'H300',
              color: 't.blue700',
              cursor: 'pointer',
            }}
            onClick={disabled ? undefined : onEdit}
          >
            <div>
              <FontAwesomeIcon icon={faPen} fontSize={14} />
            </div>
            <span>Edit</span>
          </Flex>
        ) : action === 'verify' ? (
          <Box
            as="button"
            sx={{
              width: 'fit-content',
              padding: '5px 8px',
              border: 'none',
              borderRadius: '4px',
              color: '#FFF',
              background: disabled ? '#E0E0E0' : '#0091B3',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '14px',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            onClick={disabled ? undefined : handleVerifyClick}
          >
            Verify
          </Box>
        ) : null}
      </Flex>
    </Flex>
  );
};

export default CampaignAsset;
